import { useCallback, useEffect, useRef, useState } from 'react';
import { billingApi, type RevenueSummary } from '../services/billingApi';

/**
 * ONE SOURCE FOR EVERY REVENUE FIGURE THIS ADMIN SHOWS.
 *
 * The Command Center, the Analytics page and the Tenants page all used to add
 * up `tenant.subscriptionCost` themselves — a field holding the FIRST Stripe
 * line item — and so all three quoted a number that ignored every extra
 * location and every device. They now read this, and none of them does
 * arithmetic on money.
 *
 * `error` is deliberately not swallowed into an empty summary: a dashboard that
 * renders $0.00 because a fetch failed is worse than one that says it could not
 * load, because the first is indistinguishable from a platform earning nothing.
 */
export interface RevenueSummaryState {
  summary: RevenueSummary | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useRevenueSummary(): RevenueSummaryState {
  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingApi.getRevenueSummary();
      if (!mounted.current) return;
      setSummary(res);
      setError(null);
    } catch (e: any) {
      if (!mounted.current) return;
      // The previous summary is kept on screen beside the error rather than
      // blanked — stale-but-labelled beats empty.
      setError(e?.message || 'Could not load revenue');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return { summary, loading, error, reload };
}

/** `$1,234.56`, or an em dash while the figure is genuinely unknown. */
export const revenueMoney = (cents: number | null | undefined): string =>
  cents == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
